import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Card,
  Table,
  Form,
  InputGroup,
  Button,
  Collapse
} from 'react-bootstrap';
import { 
  getGradingSystemsByOrgId,
  getCirclesByOrgId,
  getCurrentOrgId,
  getActiveGradesTranslation,
  getGradeHierarchy,
  getEmployeeStatistics,
  getActiveGradesPatersonMapping,
  getGrantedGrades,
  ORG_ID_CHANGE_EVENT
} from '../services/api';
import { FaChevronDown, FaChevronUp, FaSort, FaSortUp, FaSortDown, FaSearch } from 'react-icons/fa';


// Reusable DataTable Component with sorting, filtering, and pagination
const DataTable = ({ 
  title, 
  data, 
  columns, 
  defaultOpen = true,
  searchable = true,
  pageSize = 10 
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    return data.filter(row => 
      columns.some(col => {
        const value = col.accessor ? col.accessor(row) : row[col.key];
        return value?.toString().toLowerCase().includes(searchTerm.toLowerCase());
      })
    );
  }, [data, searchTerm, columns]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    
    return [...filteredData].sort((a, b) => {
      const aValue = columns.find(c => c.key === sortConfig.key)?.accessor 
        ? columns.find(c => c.key === sortConfig.key).accessor(a)
        : a[sortConfig.key];
      const bValue = columns.find(c => c.key === sortConfig.key)?.accessor
        ? columns.find(c => c.key === sortConfig.key).accessor(b)
        : b[sortConfig.key];
      
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      const aStr = aValue.toString().toLowerCase();
      const bStr = bValue.toString().toLowerCase();
      
      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig, columns]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, currentPage, pageSize]);

  const totalPages = Math.ceil(sortedData.length / pageSize);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ms-1" style={{ opacity: 0.3 }} />;
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="ms-1" /> 
      : <FaSortDown className="ms-1" />;
  };

  return (
    <Card className="mb-4">
      <Card.Header 
        style={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setOpen(!open)}
      >
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">{title}</h5>
          <span>
            {open ? <FaChevronUp /> : <FaChevronDown />}
          </span>
        </div>
      </Card.Header>
      <Collapse in={open}>
        <div>
          <Card.Body>
            {searchable && (
              <InputGroup className="mb-3">
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </InputGroup>
            )}
            
            <div className="table-responsive">
              <Table hover striped bordered>
                <thead>
                  <tr>
                    {columns.map(col => (
                      <th
                        key={col.key}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort(col.key)}
                      >
                        {col.header}
                        {getSortIcon(col.key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.length > 0 ? (
                    paginatedData.map((row, index) => (
                      <tr key={row.id || index}>
                        {columns.map(col => (
                          <td key={col.key}>
                            {col.render 
                              ? col.render(row[col.key], row)
                              : (col.accessor ? col.accessor(row) : row[col.key] ?? '-')
                            }
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={columns.length} className="text-center">
                        {searchTerm ? 'No results found' : 'No data available'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                  Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, sortedData.length)} of {sortedData.length} entries
                </div>
                <div>
                  <Button
                    variant="outline-success"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => prev - 1)}
                    className="me-2"
                  >
                    Previous
                  </Button>
                  <span className="mx-2">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline-success"
                    size="sm"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(prev => prev + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </Card.Body>
        </div>
      </Collapse>
    </Card>
  );
};

const DataView = () => {
  const [gradingSystems, setGradingSystems] = useState([]);
  const [circles, setCircles] = useState([]);
  const [activeGrades, setActiveGrades] = useState([]);
  const [gradeHierarchy, setGradeHierarchy] = useState([]);
  const [employeeStats, setEmployeeStats] = useState(null);
  const [patersonMapping, setPatersonMapping] = useState([]);
  const [grantedGrades, setGrantedGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orgId, setOrgId] = useState(() => {
    const currentOrgId = getCurrentOrgId();
    // Ensure we have a valid orgId, default to '169' if null or invalid
    return currentOrgId && currentOrgId !== 'null' && currentOrgId !== 'undefined' ? currentOrgId : '169';
  });
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));
  const fetchingRef = useRef(false);

  useEffect(() => {
    // Check authentication status when token changes
    const checkAuth = () => {
      setIsAuthenticated(!!localStorage.getItem('token'));
    };

    window.addEventListener('storage', checkAuth);
    
    // Ensure orgId is loaded from localStorage on mount if not already set
    const storedOrgId = localStorage.getItem('orgId');
    if (storedOrgId && storedOrgId !== 'null' && storedOrgId !== 'undefined') {
      // Only update if current orgId is invalid or different
      const currentOrgId = getCurrentOrgId();
      if (!currentOrgId || currentOrgId === 'null' || currentOrgId === 'undefined' || currentOrgId !== storedOrgId) {
        setOrgId(storedOrgId);
      }
    } else {
      // Set default if not already set
      const defaultOrgId = '169';
      setOrgId(defaultOrgId);
      localStorage.setItem('orgId', defaultOrgId);
    }
    
    return () => {
      window.removeEventListener('storage', checkAuth);
    };
  }, []);

  useEffect(() => {
    // Listen for org ID changes
    const handleOrgIdChange = (event) => {
      const detail = event.detail;
      const newOrgId = typeof detail === 'object' ? (detail.orgId || '1066') : detail || '1066';
      const source = typeof detail === 'object' ? detail.source : null;
      
      if (source === 'manual_refresh') {
        console.log(`DataView: Refreshing data for organization ID: ${newOrgId}`);
      } else if (typeof detail === 'object' && detail.previousOrgId) {
        console.log(`DataView: Organization changed from ${detail.previousOrgId || 'unknown'} to ${newOrgId}`);
      }
      
      setOrgId(newOrgId);
      if (isAuthenticated) {
        setLoading(true);
      }
    };

    window.addEventListener(ORG_ID_CHANGE_EVENT, handleOrgIdChange);
    return () => {
      window.removeEventListener(ORG_ID_CHANGE_EVENT, handleOrgIdChange);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const fetchData = async () => {
      // Prevent duplicate calls (React StrictMode causes double renders in development)
      if (fetchingRef.current) {
        return;
      }
      
      // Don't fetch if not authenticated
      if (!isAuthenticated) {
        setError('Please log in to view this data');
        setLoading(false);
        return;
      }

      // Don't fetch if orgId is not set or invalid
      if (!orgId || orgId === 'null' || orgId === 'undefined') {
        setError('Organization ID is not set. Please select an organization.');
        setLoading(false);
        return;
      }

      fetchingRef.current = true;
      
      try {
        const [gradingSystemsResponse, circlesResponse, activeGradesResponse, gradeHierarchyResponse, employeeStatsResponse, patersonMappingResponse, grantedGradesResponse] = await Promise.all([
          getGradingSystemsByOrgId(orgId),
          getCirclesByOrgId(orgId),
          getActiveGradesTranslation(orgId),
          getGradeHierarchy(orgId),
          getEmployeeStatistics(orgId),
          getActiveGradesPatersonMapping(orgId),
          getGrantedGrades(orgId)
        ]);

        // Get the result arrays from the responses
        // Handle both wrapped responses (with .result) and direct array responses
        const gradingSystemsData = gradingSystemsResponse?.result || gradingSystemsResponse || [];
        const circlesData = circlesResponse?.result || circlesResponse || [];
        const gradeHierarchyData = gradeHierarchyResponse?.result || gradeHierarchyResponse || [];
        
        // Check if we got valid data
        if (!Array.isArray(gradingSystemsData) || !Array.isArray(circlesData) || !Array.isArray(gradeHierarchyData)) {
          throw new Error('Failed to fetch data from server');
        }
        
        // Create a map for Grading System ID to Name
        const gradingSystemIdToName = {};
        gradingSystemsData.forEach(system => {
          gradingSystemIdToName[system.id] = system.gradingSystemName;
        });
        
        let activeGradesData = [];
        
        if (Array.isArray(activeGradesResponse)) {
          activeGradesData = activeGradesResponse;
        } else if (activeGradesResponse?.result && Array.isArray(activeGradesResponse.result)) {
          activeGradesData = activeGradesResponse.result;
        } else if (activeGradesResponse?.data && Array.isArray(activeGradesResponse.data)) {
          activeGradesData = activeGradesResponse.data;
        }

        activeGradesData = activeGradesData.map(grade => {
          if (typeof grade === 'string') {
            return {
              activeGrade: grade,
              baseGrade: null,
              sequence: null
            };
          }
          return grade;
        });

        const formattedGradingSystems = gradingSystemsData.map(system => ({
          id: system.id,
          name: system.gradingSystemName
        }));

        const formattedCircles = circlesData.map(circle => ({
          id: circle.id,
          name: circle.circleName
        }));

        setGradingSystems(formattedGradingSystems);
        setCircles(formattedCircles);
        setActiveGrades(activeGradesData);
        setGradeHierarchy(gradeHierarchyData.map(system => ({
          ...system,
          gradingSystemName: gradingSystemIdToName[system.gradingSystemId] || system.gradingSystemId
        })));
        setEmployeeStats(employeeStatsResponse);
        setPatersonMapping(patersonMappingResponse || []);
        setGrantedGrades(grantedGradesResponse || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        if (err.message === 'Authentication required') {
          setError('Please log in to view this data');
          setIsAuthenticated(false);
        } else {
          setError('Failed to load data. Please try again.');
        }
      } finally {
        setLoading(false);
        fetchingRef.current = false;
      }
    };

    fetchData();
  }, [orgId, isAuthenticated]);

  // Flatten grade hierarchy for table display
  // This must be called before any early returns (React Hooks rule)
  const flattenedGradeHierarchy = useMemo(() => {
    const flattened = [];
    gradeHierarchy.forEach((system) => {
      if (system.grades && Array.isArray(system.grades)) {
        system.grades.forEach((grade) => {
          flattened.push({
            id: `${system.gradingSystemId}-${grade.id}`,
            gradingSystemId: system.gradingSystemId,
            gradingSystemName: system.gradingSystemName,
            gradeName: grade.name,
            sequence: grade.sequence,
            baseGrade: grade.baseGrade,
            lowerGrade: grade.lowerGrade,
            upperGrade: grade.upperGrade,
          });
        });
      }
    });
    return flattened;
  }, [gradeHierarchy]);

  if (!isAuthenticated) {
    return (
      <div className="alert alert-warning" role="alert">
        Please log in to view this data
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div className="row g-4">
      <div className="col-12 col-lg-6">
        <DataTable
          title="Grade Systems"
          data={gradingSystems}
          columns={[
            { key: 'id', header: 'ID' },
            { key: 'name', header: 'Name' }
          ]}
          defaultOpen={true}
          pageSize={10}
        />
      </div>

      <div className="col-12 col-lg-6">
        <DataTable
          title="Circles"
          data={circles}
          columns={[
            { key: 'id', header: 'ID' },
            { key: 'name', header: 'Name' }
          ]}
          defaultOpen={true}
          pageSize={10}
        />
      </div>

      <div className="col-12">
        <DataTable
          title="Active Grades Translation"
          data={activeGrades}
          columns={[
            { key: 'activeGrade', header: 'Active Grade' },
            { key: 'baseGrade', header: 'Base Grade' },
            { key: 'lowerGrade', header: 'Lower Grade' },
            { key: 'upperGrade', header: 'Upper Grade' },
            { key: 'sequence', header: 'Sequence' }
          ]}
          defaultOpen={true}
          pageSize={15}
        />
      </div>

      {patersonMapping.length > 0 && (
        <div className="col-12">
          <DataTable
            title="Active Grades - Paterson Classic & Modern Mapping"
            data={patersonMapping}
            columns={[
              { key: 'activeGrade', header: 'Active Grade' },
              { key: 'baseGrade', header: 'Base Grade' },
              { key: 'patersonClassic', header: 'Paterson Classic' },
              { key: 'patersonModern', header: 'Paterson Modern' },
              { key: 'sequence', header: 'Sequence' }
            ]}
            defaultOpen={true}
            pageSize={15}
          />
        </div>
      )}

      {grantedGrades.length > 0 && (
        <div className="col-12">
          <DataTable
            title="Granted Grades"
            data={grantedGrades.map(grade => ({
              ...grade,
              gradingSystemName: gradingSystems.find(gs => gs.id === String(grade.GradingSystemID))?.name || grade.GradingSystemID
            }))}
            columns={[
              { key: 'GradeID', header: 'Grade ID' },
              { key: 'GradingSystemID', header: 'Grading System ID' },
              { key: 'gradingSystemName', header: 'Grading System Name' },
              { key: 'GradeName', header: 'Grade Name' },
              { key: 'Sequence', header: 'Sequence' },
              { 
                key: 'IsGranted', 
                header: 'Is Granted',
                render: (value) => value === 1 ? 'Yes' : 'No'
              }
            ]}
            defaultOpen={true}
            pageSize={15}
          />
        </div>
      )}

      <div className="col-12">
        <DataTable
          title="Grade Hierarchy"
          data={flattenedGradeHierarchy}
          columns={[
            { key: 'gradingSystemId', header: 'Grading System ID' },
            { key: 'gradingSystemName', header: 'Grading System Name' },
            { key: 'gradeName', header: 'Grade Name' },
            { key: 'sequence', header: 'Sequence' },
            { key: 'baseGrade', header: 'Base Grade' },
            { key: 'lowerGrade', header: 'Lower Grade' },
            { key: 'upperGrade', header: 'Upper Grade' }
          ]}
          defaultOpen={true}
          pageSize={20}
        />
      </div>

      {employeeStats && (
        <>
          <div className="col-12">
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Employee Statistics Summary - Data Completeness Comparison</h5>
              </Card.Header>
              <Card.Body>
                {/* Completeness Overview Table */}
                <div className="mb-4">
                  <h6 className="mb-3">Data Completeness Overview</h6>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Table</th>
                        <th>Total Count</th>
                        <th>Available Fields</th>
                        <th>Missing Fields</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><strong>Uploaded Employees</strong></td>
                        <td>{employeeStats.uploaded?.totalCount || 0}</td>
                        <td>
                          <small>
                            EmployeeNumber, Name, DateOfBirth, JobGrade, JobCode, JobTitle, CostCentre, TGP
                          </small>
                        </td>
                        <td>
                          <small className="text-muted">
                            STI, OnTargetSTI, GradeID, DateOfSalaryReview, DateOfEmployment, JobID, JobName, EmployeeID
                          </small>
                        </td>
                      </tr>
                      <tr>
                        <td><strong>Staging Employees</strong></td>
                        <td>{employeeStats.staging?.totalCount || 0}</td>
                        <td>
                          <small>
                            EmployeeNumber, Name, DateOfBirth, JobGrade, JobCode, JobTitle, CostCentre, TGP, GradeID, DateOfSalaryReview, DateOfEmployment
                          </small>
                        </td>
                        <td>
                          <small className="text-muted">
                            STI, OnTargetSTI, JobID, JobName, EmployeeID
                          </small>
                        </td>
                      </tr>
                      <tr>
                        <td><strong>Snapshot Employees</strong></td>
                        <td>{employeeStats.snapshot?.totalCount || 0}</td>
                        <td>
                          <small>
                            EmployeeNumber, EmployeeID, GradeName, JobID, JobName, TGP, STI, OnTargetSTI
                          </small>
                        </td>
                        <td>
                          <small className="text-muted">
                            Name, DateOfBirth, OrganizationJobGrade, OrganizationJobCode, OrganizationJobTitle, CostCentre, DateOfSalaryReview, DateOfEmployment, GradeID
                          </small>
                        </td>
                      </tr>
                      <tr>
                        <td><strong>Imported Employees</strong></td>
                        <td>{employeeStats.imported?.totalCount || 0}</td>
                        <td>
                          <small>
                            EmployeeNumber, Name, DateOfBirth, JobGrade, JobCode, JobTitle, CostCentre, TGP, STI
                          </small>
                        </td>
                        <td>
                          <small className="text-muted">
                            OnTargetSTI, GradeID, DateOfSalaryReview, DateOfEmployment, JobID, JobName, EmployeeID
                          </small>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>

                {/* Field Availability Matrix - Union of all fields */}
                <div className="mb-4">
                  <h6 className="mb-3">Field Availability Matrix (All Available Fields)</h6>
                  <Table striped bordered hover responsive size="sm">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th className="text-center">Uploaded</th>
                        <th className="text-center">Staging</th>
                        <th className="text-center">Snapshot</th>
                        <th className="text-center">Imported</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><strong>Employee Number</strong></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                      </tr>
                      <tr>
                        <td><strong>Employee ID</strong></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                      </tr>
                      <tr>
                        <td><strong>Name (First/Last)</strong></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                      </tr>
                      <tr>
                        <td><strong>Date of Birth</strong></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                      </tr>
                      <tr>
                        <td><strong>Organization Job Grade</strong></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                      </tr>
                      <tr>
                        <td><strong>Grade Name</strong> <small className="text-muted">(Snapshot only)</small></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                      </tr>
                      <tr>
                        <td><strong>Grade ID</strong></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                      </tr>
                      <tr>
                        <td><strong>Organization Job Code</strong></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                      </tr>
                      <tr>
                        <td><strong>Organization Job Title</strong></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                      </tr>
                      <tr>
                        <td><strong>Job ID</strong></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                      </tr>
                      <tr>
                        <td><strong>Job Name</strong> <small className="text-muted">(Snapshot only)</small></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                      </tr>
                      <tr>
                        <td><strong>Cost Centre</strong></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                      </tr>
                      <tr>
                        <td><strong>Date of Salary Review</strong></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                      </tr>
                      <tr>
                        <td><strong>Date of Employment</strong></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                      </tr>
                      <tr>
                        <td><strong>TGP</strong></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                      </tr>
                      <tr>
                        <td><strong>STI</strong></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                      </tr>
                      <tr>
                        <td><strong>On Target STI</strong></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                        <td className="text-center"><span className="badge bg-success">✓</span></td>
                        <td className="text-center"><span className="badge bg-danger">✗</span></td>
                      </tr>
                    </tbody>
                  </Table>
                </div>

                {/* Original detailed stats - keeping for reference but making it collapsible */}
                <div className="mt-4">
                  <details>
                    <summary className="h6 mb-3" style={{cursor: 'pointer'}}>Detailed Statistics (Click to expand)</summary>
                <div className="row">
                  <div className="col-md-3">
                    <h6>Uploaded Employees</h6>
                    <Table striped bordered size="sm">
                      <tbody>
                        <tr>
                          <td><strong>Total Count</strong></td>
                          <td>{employeeStats.uploaded.totalCount}</td>
                        </tr>
                        <tr>
                          <td><strong>TGP Count</strong></td>
                          <td>{employeeStats.uploaded.tgp.count}</td>
                        </tr>
                        <tr>
                          <td><strong>TGP Mean</strong></td>
                          <td>{employeeStats.uploaded.tgp.mean != null ? `R ${employeeStats.uploaded.tgp.mean.toLocaleString()}` : '—'}</td>
                        </tr>
                        <tr>
                          <td><strong>TGP Median</strong></td>
                          <td>{employeeStats.uploaded.tgp.median != null ? `R ${employeeStats.uploaded.tgp.median.toLocaleString()}` : '—'}</td>
                        </tr>
                        <tr>
                          <td><strong>TGP Min</strong></td>
                          <td>{employeeStats.uploaded.tgp.min != null ? `R ${employeeStats.uploaded.tgp.min.toLocaleString()}` : '—'}</td>
                        </tr>
                        <tr>
                          <td><strong>TGP Max</strong></td>
                          <td>{employeeStats.uploaded.tgp.max != null ? `R ${employeeStats.uploaded.tgp.max.toLocaleString()}` : '—'}</td>
                        </tr>
                        <tr>
                          <td><strong>TGP Coefficient of Variation</strong></td>
                          <td>
                            {employeeStats.uploaded.tgp.stdDev != null && employeeStats.uploaded.tgp.mean != null && employeeStats.uploaded.tgp.mean !== 0 ? (
                              `${((employeeStats.uploaded.tgp.stdDev / employeeStats.uploaded.tgp.mean) * 100).toFixed(2)}%`
                            ) : '—'}
                          </td>
                        </tr>
                        <tr>
                          <td><strong>TGP Std Dev</strong></td>
                          <td>{employeeStats.uploaded.tgp.stdDev != null ? `R ${employeeStats.uploaded.tgp.stdDev.toLocaleString()}` : '—'}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                  <div className="col-md-3">
                    <h6>Staging Employees</h6>
                    <Table striped bordered size="sm">
                      <tbody>
                        <tr>
                          <td><strong>Total Count</strong></td>
                          <td>{employeeStats.staging?.totalCount || 0}</td>
                        </tr>
                        <tr>
                          <td><strong>TGP Count</strong></td>
                          <td>{employeeStats.staging?.tgp?.count || 0}</td>
                        </tr>
                        <tr>
                          <td><strong>TGP Mean</strong></td>
                          <td>{employeeStats.staging?.tgp?.mean != null ? `R ${employeeStats.staging.tgp.mean.toLocaleString()}` : '—'}</td>
                        </tr>
                        <tr>
                          <td><strong>TGP Median</strong></td>
                          <td>{employeeStats.staging?.tgp?.median != null ? `R ${employeeStats.staging.tgp.median.toLocaleString()}` : '—'}</td>
                        </tr>
                        <tr>
                          <td><strong>TGP Min</strong></td>
                          <td>{employeeStats.staging?.tgp?.min != null ? `R ${employeeStats.staging.tgp.min.toLocaleString()}` : '—'}</td>
                        </tr>
                        <tr>
                          <td><strong>TGP Max</strong></td>
                          <td>{employeeStats.staging?.tgp?.max != null ? `R ${employeeStats.staging.tgp.max.toLocaleString()}` : '—'}</td>
                        </tr>
                        <tr>
                          <td><strong>TGP Coefficient of Variation</strong></td>
                          <td>
                            {employeeStats.staging?.tgp?.stdDev != null && employeeStats.staging?.tgp?.mean != null && employeeStats.staging.tgp.mean !== 0 ? (
                              `${((employeeStats.staging.tgp.stdDev / employeeStats.staging.tgp.mean) * 100).toFixed(2)}%`
                            ) : '—'}
                          </td>
                        </tr>
                        <tr>
                          <td><strong>TGP Std Dev</strong></td>
                          <td>{employeeStats.staging?.tgp?.stdDev != null ? `R ${employeeStats.staging.tgp.stdDev.toLocaleString()}` : '—'}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                  <div className="col-md-3">
                    <h6>Snapshot Employees</h6>
                    <Table striped bordered size="sm">
                      <tbody>
                        <tr>
                          <td><strong>Total Count</strong></td>
                          <td>{employeeStats.snapshot.totalCount}</td>
                        </tr>
                        <tr>
                          <td><strong>TGP Count</strong></td>
                          <td>{employeeStats.snapshot.tgp.count}</td>
                        </tr>
                        <tr>
                          <td><strong>TGP Mean</strong></td>
                          <td>{employeeStats.snapshot.tgp.mean != null ? `R ${employeeStats.snapshot.tgp.mean.toLocaleString()}` : '—'}</td>
                        </tr>
                        <tr>
                          <td><strong>TGP Median</strong></td>
                          <td>{employeeStats.snapshot.tgp.median != null ? `R ${employeeStats.snapshot.tgp.median.toLocaleString()}` : '—'}</td>
                        </tr>
                        <tr>
                          <td><strong>TGP Min</strong></td>
                          <td>{employeeStats.snapshot.tgp.min != null ? `R ${employeeStats.snapshot.tgp.min.toLocaleString()}` : '—'}</td>
                        </tr>
                        <tr>
                          <td><strong>TGP Max</strong></td>
                          <td>{employeeStats.snapshot.tgp.max != null ? `R ${employeeStats.snapshot.tgp.max.toLocaleString()}` : '—'}</td>
                        </tr>
                        <tr>
                          <td><strong>TGP Coefficient of Variation</strong></td>
                          <td>
                            {employeeStats.snapshot.tgp.stdDev != null && employeeStats.snapshot.tgp.mean != null && employeeStats.snapshot.tgp.mean !== 0 ? (
                              `${((employeeStats.snapshot.tgp.stdDev / employeeStats.snapshot.tgp.mean) * 100).toFixed(2)}%`
                            ) : '—'}
                          </td>
                        </tr>
                        <tr>
                          <td><strong>TGP Std Dev</strong></td>
                          <td>{employeeStats.snapshot.tgp.stdDev != null ? `R ${employeeStats.snapshot.tgp.stdDev.toLocaleString()}` : '—'}</td>
                        </tr>
                        {employeeStats.snapshot.sti && (
                          <>
                            <tr>
                              <td><strong>STI Count</strong></td>
                              <td>{employeeStats.snapshot.sti.count}</td>
                            </tr>
                            <tr>
                              <td><strong>STI Mean</strong></td>
                              <td>{employeeStats.snapshot.sti.mean != null ? `R ${employeeStats.snapshot.sti.mean.toLocaleString()}` : '—'}</td>
                            </tr>
                            <tr>
                              <td><strong>On Target STI Mean</strong></td>
                              <td>{employeeStats.snapshot.onTargetSti?.mean != null ? `R ${employeeStats.snapshot.onTargetSti.mean.toLocaleString()}` : '—'}</td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </Table>
                  </div>
                  <div className="col-md-3">
                    <h6>Imported Employees</h6>
                    <Table striped bordered size="sm">
                      <tbody>
                        <tr>
                          <td><strong>Total Count</strong></td>
                          <td>{employeeStats.imported?.totalCount || 0}</td>
                        </tr>
                        <tr>
                          <td><strong>TGP Count</strong></td>
                          <td>{employeeStats.imported?.tgp?.count || 0}</td>
                        </tr>
                        <tr>
                          <td><strong>TGP Mean</strong></td>
                          <td>{employeeStats.imported?.tgp?.mean != null ? `R ${employeeStats.imported.tgp.mean.toLocaleString()}` : '—'}</td>
                        </tr>
                        <tr>
                          <td><strong>TGP Median</strong></td>
                          <td>{employeeStats.imported?.tgp?.median != null ? `R ${employeeStats.imported.tgp.median.toLocaleString()}` : '—'}</td>
                        </tr>
                        <tr>
                          <td><strong>TGP Min</strong></td>
                          <td>{employeeStats.imported?.tgp?.min != null ? `R ${employeeStats.imported.tgp.min.toLocaleString()}` : '—'}</td>
                        </tr>
                        <tr>
                          <td><strong>TGP Max</strong></td>
                          <td>{employeeStats.imported?.tgp?.max != null ? `R ${employeeStats.imported.tgp.max.toLocaleString()}` : '—'}</td>
                        </tr>
                        <tr>
                          <td><strong>TGP Coefficient of Variation</strong></td>
                          <td>
                            {employeeStats.imported?.tgp?.stdDev != null && employeeStats.imported?.tgp?.mean != null && employeeStats.imported.tgp.mean !== 0 ? (
                              `${((employeeStats.imported.tgp.stdDev / employeeStats.imported.tgp.mean) * 100).toFixed(2)}%`
                            ) : '—'}
                          </td>
                        </tr>
                        <tr>
                          <td><strong>TGP Std Dev</strong></td>
                          <td>{employeeStats.imported?.tgp?.stdDev != null ? `R ${employeeStats.imported.tgp.stdDev.toLocaleString()}` : '—'}</td>
                        </tr>
                        {employeeStats.imported?.sti && (
                          <>
                            <tr>
                              <td><strong>STI Count</strong></td>
                              <td>{employeeStats.imported.sti.count}</td>
                            </tr>
                            <tr>
                              <td><strong>STI Mean</strong></td>
                              <td>{employeeStats.imported.sti.mean != null ? `R ${employeeStats.imported.sti.mean.toLocaleString()}` : '—'}</td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </div>
                  </details>
                </div>
              </Card.Body>
            </Card>
          </div>

          <div className="col-12">
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">TGP Comparison</h5>
              </Card.Header>
              <Card.Body>
                <Table striped bordered>
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th>Uploaded Employees</th>
                      <th>Staging Employees</th>
                      <th>Snapshot Employees</th>
                      <th>Imported Employees</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Count</strong></td>
                      <td>{employeeStats.uploaded?.tgp?.count || 0}</td>
                      <td>{employeeStats.staging?.tgp?.count || 0}</td>
                      <td>{employeeStats.snapshot?.tgp?.count || 0}</td>
                      <td>{employeeStats.imported?.tgp?.count || 0}</td>
                    </tr>
                    <tr>
                      <td><strong>Mean</strong></td>
                      <td>{employeeStats.uploaded?.tgp?.mean != null ? `R ${employeeStats.uploaded.tgp.mean.toLocaleString()}` : '—'}</td>
                      <td>{employeeStats.staging?.tgp?.mean != null ? `R ${employeeStats.staging.tgp.mean.toLocaleString()}` : '—'}</td>
                      <td>{employeeStats.snapshot?.tgp?.mean != null ? `R ${employeeStats.snapshot.tgp.mean.toLocaleString()}` : '—'}</td>
                      <td>{employeeStats.imported?.tgp?.mean != null ? `R ${employeeStats.imported.tgp.mean.toLocaleString()}` : '—'}</td>
                    </tr>
                    <tr>
                      <td><strong>Median</strong></td>
                      <td>{employeeStats.uploaded?.tgp?.median != null ? `R ${employeeStats.uploaded.tgp.median.toLocaleString()}` : '—'}</td>
                      <td>{employeeStats.staging?.tgp?.median != null ? `R ${employeeStats.staging.tgp.median.toLocaleString()}` : '—'}</td>
                      <td>{employeeStats.snapshot?.tgp?.median != null ? `R ${employeeStats.snapshot.tgp.median.toLocaleString()}` : '—'}</td>
                      <td>{employeeStats.imported?.tgp?.median != null ? `R ${employeeStats.imported.tgp.median.toLocaleString()}` : '—'}</td>
                    </tr>
                    <tr>
                      <td><strong>Min</strong></td>
                      <td>{employeeStats.uploaded?.tgp?.min != null ? `R ${employeeStats.uploaded.tgp.min.toLocaleString()}` : '—'}</td>
                      <td>{employeeStats.staging?.tgp?.min != null ? `R ${employeeStats.staging.tgp.min.toLocaleString()}` : '—'}</td>
                      <td>{employeeStats.snapshot?.tgp?.min != null ? `R ${employeeStats.snapshot.tgp.min.toLocaleString()}` : '—'}</td>
                      <td>{employeeStats.imported?.tgp?.min != null ? `R ${employeeStats.imported.tgp.min.toLocaleString()}` : '—'}</td>
                    </tr>
                    <tr>
                      <td><strong>Max</strong></td>
                      <td>{employeeStats.uploaded?.tgp?.max != null ? `R ${employeeStats.uploaded.tgp.max.toLocaleString()}` : '—'}</td>
                      <td>{employeeStats.staging?.tgp?.max != null ? `R ${employeeStats.staging.tgp.max.toLocaleString()}` : '—'}</td>
                      <td>{employeeStats.snapshot?.tgp?.max != null ? `R ${employeeStats.snapshot.tgp.max.toLocaleString()}` : '—'}</td>
                      <td>{employeeStats.imported?.tgp?.max != null ? `R ${employeeStats.imported.tgp.max.toLocaleString()}` : '—'}</td>
                    </tr>
                    <tr>
                      <td><strong>Coefficient of Variation</strong></td>
                      <td>
                        {employeeStats.uploaded?.tgp?.stdDev != null && employeeStats.uploaded?.tgp?.mean != null && employeeStats.uploaded.tgp.mean !== 0 
                          ? `${((employeeStats.uploaded.tgp.stdDev / employeeStats.uploaded.tgp.mean) * 100).toFixed(2)}%` 
                          : '—'}
                      </td>
                      <td>
                        {employeeStats.staging?.tgp?.stdDev != null && employeeStats.staging?.tgp?.mean != null && employeeStats.staging.tgp.mean !== 0 
                          ? `${((employeeStats.staging.tgp.stdDev / employeeStats.staging.tgp.mean) * 100).toFixed(2)}%` 
                          : '—'}
                      </td>
                      <td>
                        {employeeStats.snapshot?.tgp?.stdDev != null && employeeStats.snapshot?.tgp?.mean != null && employeeStats.snapshot.tgp.mean !== 0 
                          ? `${((employeeStats.snapshot.tgp.stdDev / employeeStats.snapshot.tgp.mean) * 100).toFixed(2)}%` 
                          : '—'}
                      </td>
                      <td>
                        {employeeStats.imported?.tgp?.stdDev != null && employeeStats.imported?.tgp?.mean != null && employeeStats.imported.tgp.mean !== 0 
                          ? `${((employeeStats.imported.tgp.stdDev / employeeStats.imported.tgp.mean) * 100).toFixed(2)}%` 
                          : '—'}
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Std Dev</strong></td>
                      <td>{employeeStats.uploaded?.tgp?.stdDev != null ? `R ${employeeStats.uploaded.tgp.stdDev.toLocaleString()}` : '—'}</td>
                      <td>{employeeStats.staging?.tgp?.stdDev != null ? `R ${employeeStats.staging.tgp.stdDev.toLocaleString()}` : '—'}</td>
                      <td>{employeeStats.snapshot?.tgp?.stdDev != null ? `R ${employeeStats.snapshot.tgp.stdDev.toLocaleString()}` : '—'}</td>
                      <td>{employeeStats.imported?.tgp?.stdDev != null ? `R ${employeeStats.imported.tgp.stdDev.toLocaleString()}` : '—'}</td>
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </div>

          <div className="col-12">
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Delta & Variance Analysis</h5>
              </Card.Header>
              <Card.Body>
                <div className="row">
                  <div className="col-md-3">
                    <h6>Delta: Uploaded → Staging</h6>
                    <Table striped bordered size="sm">
                      <tbody>
                        <tr>
                          <td><strong>Count Delta</strong></td>
                          <td>
                            {employeeStats.delta?.uploadedToStaging?.count?.absolute || 0} 
                            {employeeStats.delta?.uploadedToStaging?.count?.percent != null && (
                              <span className={`ms-2 ${employeeStats.delta.uploadedToStaging.count.percent >= 0 ? 'text-success' : 'text-danger'}`}>
                                ({employeeStats.delta.uploadedToStaging.count.percent >= 0 ? '+' : ''}{employeeStats.delta.uploadedToStaging.count.percent}%)
                              </span>
                            )}
                          </td>
                        </tr>
                        {employeeStats.delta?.uploadedToStaging?.tgp && (
                          <tr>
                            <td><strong>TGP Mean Delta</strong></td>
                            <td>
                              {employeeStats.delta.uploadedToStaging.tgp.absolute >= 0 ? '+' : ''}R {employeeStats.delta.uploadedToStaging.tgp.absolute.toLocaleString()}
                              {employeeStats.delta.uploadedToStaging.tgp.percent != null && (
                                <span className={`ms-2 ${employeeStats.delta.uploadedToStaging.tgp.percent >= 0 ? 'text-success' : 'text-danger'}`}>
                                  ({employeeStats.delta.uploadedToStaging.tgp.percent >= 0 ? '+' : ''}{employeeStats.delta.uploadedToStaging.tgp.percent}%)
                                </span>
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                  <div className="col-md-3">
                    <h6>Delta: Uploaded → Snapshot</h6>
                    <Table striped bordered size="sm">
                      <tbody>
                        <tr>
                          <td><strong>Count Delta</strong></td>
                          <td>
                            {employeeStats.delta?.uploadedToSnapshot?.count?.absolute || 0} 
                            {employeeStats.delta?.uploadedToSnapshot?.count?.percent != null && (
                              <span className={`ms-2 ${employeeStats.delta.uploadedToSnapshot.count.percent >= 0 ? 'text-success' : 'text-danger'}`}>
                                ({employeeStats.delta.uploadedToSnapshot.count.percent >= 0 ? '+' : ''}{employeeStats.delta.uploadedToSnapshot.count.percent}%)
                              </span>
                            )}
                          </td>
                        </tr>
                        {employeeStats.delta?.uploadedToSnapshot?.tgp && (
                          <tr>
                            <td><strong>TGP Mean Delta</strong></td>
                            <td>
                              {employeeStats.delta.uploadedToSnapshot.tgp.absolute >= 0 ? '+' : ''}R {employeeStats.delta.uploadedToSnapshot.tgp.absolute.toLocaleString()}
                              {employeeStats.delta.uploadedToSnapshot.tgp.percent != null && (
                                <span className={`ms-2 ${employeeStats.delta.uploadedToSnapshot.tgp.percent >= 0 ? 'text-success' : 'text-danger'}`}>
                                  ({employeeStats.delta.uploadedToSnapshot.tgp.percent >= 0 ? '+' : ''}{employeeStats.delta.uploadedToSnapshot.tgp.percent}%)
                                </span>
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                  <div className="col-md-3">
                    <h6>Delta: Staging → Snapshot</h6>
                    <Table striped bordered size="sm">
                      <tbody>
                        <tr>
                          <td><strong>Count Delta</strong></td>
                          <td>
                            {employeeStats.delta?.stagingToSnapshot?.count?.absolute || 0} 
                            {employeeStats.delta?.stagingToSnapshot?.count?.percent != null && (
                              <span className={`ms-2 ${employeeStats.delta.stagingToSnapshot.count.percent >= 0 ? 'text-success' : 'text-danger'}`}>
                                ({employeeStats.delta.stagingToSnapshot.count.percent >= 0 ? '+' : ''}{employeeStats.delta.stagingToSnapshot.count.percent}%)
                              </span>
                            )}
                          </td>
                        </tr>
                        {employeeStats.delta?.stagingToSnapshot?.tgp && (
                          <tr>
                            <td><strong>TGP Mean Delta</strong></td>
                            <td>
                              {employeeStats.delta.stagingToSnapshot.tgp.absolute >= 0 ? '+' : ''}R {employeeStats.delta.stagingToSnapshot.tgp.absolute.toLocaleString()}
                              {employeeStats.delta.stagingToSnapshot.tgp.percent != null && (
                                <span className={`ms-2 ${employeeStats.delta.stagingToSnapshot.tgp.percent >= 0 ? 'text-success' : 'text-danger'}`}>
                                  ({employeeStats.delta.stagingToSnapshot.tgp.percent >= 0 ? '+' : ''}{employeeStats.delta.stagingToSnapshot.tgp.percent}%)
                                </span>
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                  <div className="col-md-3">
                    <h6>Delta: Uploaded → Imported</h6>
                    <Table striped bordered size="sm">
                      <tbody>
                        <tr>
                          <td><strong>Count Delta</strong></td>
                          <td>
                            {employeeStats.delta?.uploadedToImported?.count?.absolute || 0} 
                            {employeeStats.delta?.uploadedToImported?.count?.percent != null && (
                              <span className={`ms-2 ${employeeStats.delta.uploadedToImported.count.percent >= 0 ? 'text-success' : 'text-danger'}`}>
                                ({employeeStats.delta.uploadedToImported.count.percent >= 0 ? '+' : ''}{employeeStats.delta.uploadedToImported.count.percent}%)
                              </span>
                            )}
                          </td>
                        </tr>
                        {employeeStats.delta?.uploadedToImported?.tgp && (
                          <tr>
                            <td><strong>TGP Mean Delta</strong></td>
                            <td>
                              {employeeStats.delta.uploadedToImported.tgp.absolute >= 0 ? '+' : ''}R {employeeStats.delta.uploadedToImported.tgp.absolute.toLocaleString()}
                              {employeeStats.delta.uploadedToImported.tgp.percent != null && (
                                <span className={`ms-2 ${employeeStats.delta.uploadedToImported.tgp.percent >= 0 ? 'text-success' : 'text-danger'}`}>
                                  ({employeeStats.delta.uploadedToImported.tgp.percent >= 0 ? '+' : ''}{employeeStats.delta.uploadedToImported.tgp.percent}%)
                                </span>
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-md-4">
                    <h6>Delta: Staging → Imported</h6>
                    <Table striped bordered size="sm">
                      <tbody>
                        <tr>
                          <td><strong>Count Delta</strong></td>
                          <td>
                            {employeeStats.delta?.stagingToImported?.count?.absolute || 0} 
                            {employeeStats.delta?.stagingToImported?.count?.percent != null && (
                              <span className={`ms-2 ${employeeStats.delta.stagingToImported.count.percent >= 0 ? 'text-success' : 'text-danger'}`}>
                                ({employeeStats.delta.stagingToImported.count.percent >= 0 ? '+' : ''}{employeeStats.delta.stagingToImported.count.percent}%)
                              </span>
                            )}
                          </td>
                        </tr>
                        {employeeStats.delta?.stagingToImported?.tgp && (
                          <tr>
                            <td><strong>TGP Mean Delta</strong></td>
                            <td>
                              {employeeStats.delta.stagingToImported.tgp.absolute >= 0 ? '+' : ''}R {employeeStats.delta.stagingToImported.tgp.absolute.toLocaleString()}
                              {employeeStats.delta.stagingToImported.tgp.percent != null && (
                                <span className={`ms-2 ${employeeStats.delta.stagingToImported.tgp.percent >= 0 ? 'text-success' : 'text-danger'}`}>
                                  ({employeeStats.delta.stagingToImported.tgp.percent >= 0 ? '+' : ''}{employeeStats.delta.stagingToImported.tgp.percent}%)
                                </span>
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                  <div className="col-md-4">
                    <h6>Delta: Snapshot → Imported</h6>
                    <Table striped bordered size="sm">
                      <tbody>
                        <tr>
                          <td><strong>Count Delta</strong></td>
                          <td>
                            {employeeStats.delta?.snapshotToImported?.count?.absolute || 0} 
                            {employeeStats.delta?.snapshotToImported?.count?.percent != null && (
                              <span className={`ms-2 ${employeeStats.delta.snapshotToImported.count.percent >= 0 ? 'text-success' : 'text-danger'}`}>
                                ({employeeStats.delta.snapshotToImported.count.percent >= 0 ? '+' : ''}{employeeStats.delta.snapshotToImported.count.percent}%)
                              </span>
                            )}
                          </td>
                        </tr>
                        {employeeStats.delta?.snapshotToImported?.tgp && (
                          <tr>
                            <td><strong>TGP Mean Delta</strong></td>
                            <td>
                              {employeeStats.delta.snapshotToImported.tgp.absolute >= 0 ? '+' : ''}R {employeeStats.delta.snapshotToImported.tgp.absolute.toLocaleString()}
                              {employeeStats.delta.snapshotToImported.tgp.percent != null && (
                                <span className={`ms-2 ${employeeStats.delta.snapshotToImported.tgp.percent >= 0 ? 'text-success' : 'text-danger'}`}>
                                  ({employeeStats.delta.snapshotToImported.tgp.percent >= 0 ? '+' : ''}{employeeStats.delta.snapshotToImported.tgp.percent}%)
                                </span>
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-12">
                    <h6>Variance Percentage Differences</h6>
                    <Table striped bordered size="sm">
                      <tbody>
                        {employeeStats.variance?.tgp?.uploadedToStaging?.percent != null && (
                          <tr>
                            <td><strong>Uploaded → Staging</strong></td>
                            <td>
                              <span className={employeeStats.variance.tgp.uploadedToStaging.percent >= 0 ? 'text-success' : 'text-danger'}>
                                {employeeStats.variance.tgp.uploadedToStaging.percent >= 0 ? '+' : ''}{employeeStats.variance.tgp.uploadedToStaging.percent}%
                              </span>
                            </td>
                          </tr>
                        )}
                        {employeeStats.variance?.tgp?.uploadedToSnapshot?.percent != null && (
                          <tr>
                            <td><strong>Uploaded → Snapshot</strong></td>
                            <td>
                              <span className={employeeStats.variance.tgp.uploadedToSnapshot.percent >= 0 ? 'text-success' : 'text-danger'}>
                                {employeeStats.variance.tgp.uploadedToSnapshot.percent >= 0 ? '+' : ''}{employeeStats.variance.tgp.uploadedToSnapshot.percent}%
                              </span>
                            </td>
                          </tr>
                        )}
                        {employeeStats.variance?.tgp?.uploadedToImported?.percent != null && (
                          <tr>
                            <td><strong>Uploaded → Imported</strong></td>
                            <td>
                              <span className={employeeStats.variance.tgp.uploadedToImported.percent >= 0 ? 'text-success' : 'text-danger'}>
                                {employeeStats.variance.tgp.uploadedToImported.percent >= 0 ? '+' : ''}{employeeStats.variance.tgp.uploadedToImported.percent}%
                              </span>
                            </td>
                          </tr>
                        )}
                        {employeeStats.variance?.tgp?.stagingToSnapshot?.percent != null && (
                          <tr>
                            <td><strong>Staging → Snapshot</strong></td>
                            <td>
                              <span className={employeeStats.variance.tgp.stagingToSnapshot.percent >= 0 ? 'text-success' : 'text-danger'}>
                                {employeeStats.variance.tgp.stagingToSnapshot.percent >= 0 ? '+' : ''}{employeeStats.variance.tgp.stagingToSnapshot.percent}%
                              </span>
                            </td>
                          </tr>
                        )}
                        {employeeStats.variance?.tgp?.stagingToImported?.percent != null && (
                          <tr>
                            <td><strong>Staging → Imported</strong></td>
                            <td>
                              <span className={employeeStats.variance.tgp.stagingToImported.percent >= 0 ? 'text-success' : 'text-danger'}>
                                {employeeStats.variance.tgp.stagingToImported.percent >= 0 ? '+' : ''}{employeeStats.variance.tgp.stagingToImported.percent}%
                              </span>
                            </td>
                          </tr>
                        )}
                        {employeeStats.variance?.tgp?.snapshotToImported?.percent != null && (
                          <tr>
                            <td><strong>Snapshot → Imported</strong></td>
                            <td>
                              <span className={employeeStats.variance.tgp.snapshotToImported.percent >= 0 ? 'text-success' : 'text-danger'}>
                                {employeeStats.variance.tgp.snapshotToImported.percent >= 0 ? '+' : ''}{employeeStats.variance.tgp.snapshotToImported.percent}%
                              </span>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>

          <div className="col-12 col-lg-6">
            <DataTable
              title="Uploaded Employees - Grade Distribution"
              data={Object.entries(employeeStats.uploaded.gradeDistribution || {}).map(([grade, count]) => ({
                grade,
                count
              }))}
              columns={[
                { key: 'grade', header: 'Grade' },
                { key: 'count', header: 'Count' }
              ]}
              defaultOpen={true}
              pageSize={10}
            />
          </div>

          <div className="col-12 col-lg-6">
            <DataTable
              title="Snapshot Employees - Grade Distribution"
              data={Object.entries(employeeStats.snapshot.gradeDistribution || {}).map(([grade, count]) => ({
                grade,
                count
              }))}
              columns={[
                { key: 'grade', header: 'Grade' },
                { key: 'count', header: 'Count' }
              ]}
              defaultOpen={true}
              pageSize={10}
            />
          </div>

          <div className="col-12 col-lg-6">
            <DataTable
              title="Staging Employees - Grade Distribution"
              data={Object.entries(employeeStats.staging?.gradeDistribution || {}).map(([grade, count]) => ({
                grade,
                count
              }))}
              columns={[
                { key: 'grade', header: 'Grade' },
                { key: 'count', header: 'Count' }
              ]}
              defaultOpen={true}
              pageSize={10}
            />
          </div>

          <div className="col-12 col-lg-6">
            <DataTable
              title="Imported Employees - Grade Distribution"
              data={Object.entries(employeeStats.imported?.gradeDistribution || {}).map(([grade, count]) => ({
                grade,
                count
              }))}
              columns={[
                { key: 'grade', header: 'Grade' },
                { key: 'count', header: 'Count' }
              ]}
              defaultOpen={true}
              pageSize={10}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default DataView; 